import React from 'react';
import { motion } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Calendar, Flag, User, Briefcase } from 'lucide-react';

const About = () => {
  const { t } = useLanguage();

  const details = [
    { icon: MapPin, label: t('about.details.location_label'), value: t('about.location') },
    { icon: Calendar, label: t('about.details.age_label'), value: t('about.age') },
    { icon: Flag, label: t('about.details.nationality_label'), value: t('about.nationality') },
    { icon: Briefcase, label: t('about.details.profesion_label'), value: t('about.profesion') },
  ];

  return (
    <section id="about" className="py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Image Container */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative flex-shrink-0"
          >
            <div className="w-64 h-64 md:w-80 md:h-80 rounded-3xl overflow-hidden border-2 border-primary/30 relative z-10">
              <img
                src="/assets/portfolio/cristobal-bello.png"
                alt="Cristobal Bello"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                referrerPolicy="no-referrer"
              />
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-secondary/20 rounded-full blur-2xl -z-10" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-primary/20 rounded-full blur-2xl -z-10" />
            <div className="absolute inset-0 border-2 border-primary/20 rounded-3xl translate-x-4 translate-y-4 -z-10" />
          </motion.div>

          {/* Content Container */}
          <div className="flex-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-display font-black mb-8">
                {t('about.title')}
              </h2>
              <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-10">
                {t('about.description')}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {details.map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-center gap-4 p-4 glass rounded-2xl group hover:border-primary/30 transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-mono uppercase tracking-widest text-white/40 mb-1">
                        {item.label}
                      </p>
                      <p className="font-bold text-white/90">{item.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
